import { createElement as _createElement } from "@emotion/react";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import clsx from "clsx";
import { memo, useEffect, useRef, useState } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList } from "react-window";
import { ExpandMoreOutline } from "../icons/icons";
import { mergeSx } from "../system";
import { Box } from "./Box";
import { FloatingFocusManager, FloatingPortal, autoUpdate, flip, offset, size, useDismiss, useFloating, useInteractions, useListNavigation, useRole, useTypeahead, } from "@floating-ui/react";
import { Button, buttonClasses } from "./Button";
import { Flag } from "./Flag";
import { TextField } from "./TextField";
import { textInputBaseClasses } from "./TextInputBase";
import { MenuItem } from "./MenuItem";
import { MenuPopper } from "./MenuPopper";
export const phoneFieldClasses = {
    root: "MinervaPhoneField-root",
    "country-code-selector": "MinervaPhoneField-countryCodeSelector",
    "country-code-selector-button": "MinervaPhoneField-countryCodeSelectorButton",
    "country-code-selector-menu-item": "MinervaPhoneField-countryCodeSelectorMenuItem",
};
const predefinedCountryCodes = [
    {
        value: 1,
        label: "Canada",
        icon: _jsx(Flag, { country: "canada", size: "sm" }),
    },
    {
        value: 1,
        label: "United States",
        icon: _jsx(Flag, { country: "unitedStatesOfAmerica", size: "sm" }),
    },
];
export const unformatPhoneNumber = (value) => value.replace(/\D/g, "");
export const formatPhoneNumber = (value) => value.replace(/(\d{3})?(?:(\d{3})?(\d{4})?)?/, (_, areaCode, firstThree, lastFour) => {
    const areaCodeFormatted = areaCode ? `(${areaCode})` : "";
    const firstThreeFormatted = firstThree ? ` ${firstThree}` : "";
    const lastFourFormatted = lastFour ? `-${lastFour}` : "";
    return areaCodeFormatted + firstThreeFormatted + lastFourFormatted;
});
export const PhoneField = memo(({ placeholder = "(123) 123-1234", countryCodes, defaultCountryCode = "Canada", className, value: _value, onChange, sx, disableFormatting = false, removePredefinedCountryCodes = false, ...rest }) => {
    const [open, setOpen] = useState(false);
    const availableCountryCodes = countryCodes && removePredefinedCountryCodes
        ? countryCodes
        : countryCodes
            ? [...predefinedCountryCodes, ...countryCodes]
            : predefinedCountryCodes;
    const [countryCode, setCountryCode] = useState(availableCountryCodes.find((a) => a.label === defaultCountryCode) ??
        availableCountryCodes.find((a) => a.label === "Canada") ??
        availableCountryCodes[0]);
    const [value, setValue] = useState(_value ? unformatPhoneNumber(_value) : "");
    const inputRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(null);
    const navigationRef = useRef([]);
    const typeaheadRef = useRef([]);
    const { refs, floatingStyles, context } = useFloating({
        whileElementsMounted: autoUpdate,
        placement: "bottom-start",
        open,
        onOpenChange: setOpen,
        middleware: [
            offset(14),
            flip({ padding: 4 }),
            size({
                apply({ availableHeight, elements }) {
                    Object.assign(elements.floating.style, {
                        maxHeight: `${availableHeight * 0.85}px`,
                        width: "100%",
                        maxWidth: "200px",
                    });
                },
            }),
        ],
    });
    const dismiss = useDismiss(context);
    const listNav = useListNavigation(context, {
        activeIndex,
        listRef: navigationRef,
        onNavigate: setActiveIndex,
        openOnArrowKeyDown: false,
    });
    const typeahead = useTypeahead(context, {
        listRef: typeaheadRef,
        activeIndex,
        onMatch: setActiveIndex,
        enabled: availableCountryCodes.length <= 50,
    });
    const role = useRole(context, {
        role: "menu",
    });
    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
        dismiss,
        role,
        listNav,
        typeahead,
    ]);
    const handleChange = (e) => {
        //Input masking achieved following this article: https://dev.to/juanmanuelcrego/input-mask-in-react-without-libraries-5akf
        if (inputRef.current) {
            if (!disableFormatting) {
                // Get the cursor position
                const cursorPosition = inputRef.current.selectionStart;
                if (cursorPosition === null) {
                    return;
                }
                // Count the initial number of non-digit characters before the cursor
                const initialNonDigits = (inputRef.current.value.substring(0, cursorPosition).match(/\D/g) || []).length;
                // Remove all non-digit characters
                const value = inputRef.current.value.replace(/\D/g, "").substring(0, 10);
                // Update the ref and the state
                // Mutating the ref directly is necessary to prevent formatting issues with fast typing
                inputRef.current.value = formatPhoneNumber(value);
                setValue(inputRef.current.value);
                // Count the final number of non-digit characters before the cursor
                const finalNonDigits = (inputRef.current.value.substring(0, cursorPosition).match(/\D/g) || []).length;
                const deltaNonDigits = finalNonDigits - initialNonDigits;
                // Restore the cursor position
                if (cursorPosition !== null) {
                    const newCursorPosition = cursorPosition + deltaNonDigits;
                    inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
                }
            }
            else {
                setValue(unformatPhoneNumber(inputRef.current.value));
            }
            const raw = unformatPhoneNumber(inputRef.current.value);
            onChange?.(e, { countryCode: countryCode?.value, phoneNumber: raw });
        }
    };
    useEffect(() => {
        if (_value) {
            if (!disableFormatting) {
                // Check if the value starts with a country code
                const unformatted = unformatPhoneNumber(_value);
                if (unformatted.length > 10) {
                    const countryCodeValue = Number.parseInt(unformatted.slice(0, unformatted.length - 10));
                    setValue(formatPhoneNumber(unformatted.slice(-10)));
                    setCountryCode(availableCountryCodes.find((c) => c.value === countryCodeValue) ?? {
                        value: countryCodeValue,
                    });
                }
                else {
                    setValue(formatPhoneNumber(unformatted));
                }
            }
            else {
                setValue(unformatPhoneNumber(_value));
            }
        }
    }, [_value]);
    useEffect(() => {
        onChange?.(undefined, {
            countryCode: countryCode?.value,
            phoneNumber: unformatPhoneNumber(value),
        });
    }, [countryCode]);
    return (_jsxs(_Fragment, { children: [_jsx(TextField, { ref: inputRef, placeholder: placeholder, value: value, type: "tel", maxLength: disableFormatting ? undefined : 14, inputMode: "numeric", className: clsx([phoneFieldClasses.root, className]), onChange: (e) => handleChange(e), sx: mergeSx({
                    [`& .${textInputBaseClasses["input-root"]}`]: {
                        paddingLeft: 0,
                    },
                }, sx), startNode: _jsxs(Button, { color: "surface", variant: "minimal", startNode: countryCode?.icon, endNode: _jsx(ExpandMoreOutline, {}), className: phoneFieldClasses["country-code-selector-button"], ...getReferenceProps({
                        onClick() {
                            if (open) {
                                setOpen(false);
                            }
                            else {
                                setOpen(true);
                                setActiveIndex(null);
                            }
                        },
                        onKeyDown(event) {
                            if (event.key === "ArrowDown" && !open) {
                                setOpen(true);
                                setActiveIndex(null);
                            }
                        },
                    }), ref: refs.setReference, sx: {
                        [`&.${buttonClasses.minimal}`]: {
                            paddingLeft: 3,
                        },
                    }, children: [countryCode?.value && "+", countryCode?.value] }), ...rest }), open && (_jsx(FloatingPortal, { children: _jsx(FloatingFocusManager, { context: context, children: _jsx(MenuPopper, { ...getFloatingProps({
                            ref: refs.setFloating,
                            style: floatingStyles,
                        }), sx: {
                            overflow: "hidden",
                        }, children: _jsx(Box
                        // LIMITATION: The height of the menu items is assumed to be 40px. Doing this prevents the need to measure each item's height in a pre-render pass.
                        , { 
                            // LIMITATION: The height of the menu items is assumed to be 40px. Doing this prevents the need to measure each item's height in a pre-render pass.
                            height: `${availableCountryCodes.length * 40}px`, width: "100%", children: _jsx(AutoSizer, { disableWidth: true, children: ({ height }) => (_jsx(VariableSizeList, { width: "100%", height: height, itemCount: availableCountryCodes.length, itemSize: () => 40, children: ({ index, style }) => {
                                        const item = availableCountryCodes[index];
                                        return item ? (_createElement(MenuItem, { ...getItemProps({
                                                ref: (node) => {
                                                    if (node) {
                                                        navigationRef.current[index] = node;
                                                        typeaheadRef.current[index] =
                                                            node?.textContent ??
                                                                null;
                                                    }
                                                },
                                            }), key: `${index}-${item.label}`, role: "option", selected: countryCode?.label === item.label &&
                                                item.value === countryCode?.value, focused: activeIndex === index, startNode: item.icon, label: `${item.label ?? ""} (+${item.value})`, ...item, style: style, className: phoneFieldClasses["country-code-selector-menu-item"], density: "dense", onClick: (e) => {
                                                e.stopPropagation();
                                                setCountryCode(item);
                                                setOpen(false);
                                            } })) : null;
                                    } })) }) }) }) }) }))] }));
});
//# sourceMappingURL=PhoneField.js.map